import { BarChart3, Building2, Gavel, Target, Trophy, Users } from "lucide-react";
import { Form, Link, useLocation, useNavigation, useRevalidator } from "react-router";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { TenderCard } from "~/components/tenders/tender-card";
import { ClientOnly } from "~/components/ui/client-only";
import { DataLoadError } from "~/components/ui/data-load-error";
import {
	companyCanonicalUrl,
	companyFromMatches,
	companyNotFoundMeta,
	companySocialMeta,
	parseIcoSlug,
	useCompanyProfile,
} from "~/lib/company-profile";
import { formatCurrency, formatPercent } from "~/lib/format";
import { useViewer } from "~/lib/hooks/use-viewer";
import { generateMetaTags } from "~/lib/seo";
import {
	fetchCompanyTenderCompetitors,
	fetchCompanyTenderStats,
	fetchCompanyTenders,
	tenderCountLabel,
	winCountLabel,
} from "~/lib/tenders";
import type { Route } from "./+types/zakazky";

export async function loader({ params, request }: Route.LoaderArgs) {
	const ico = parseIcoSlug(params.icoSlug);
	const url = new URL(request.url);
	const listParams = new URLSearchParams();
	for (const key of ["role", "dateFrom", "dateTo", "page"]) {
		const value = url.searchParams.get(key);
		if (value) listParams.set(key, value);
	}
	listParams.set("limit", "20");

	const [tenders, stats, competitors] = await Promise.allSettled([
		fetchCompanyTenders(ico, listParams, request.signal),
		fetchCompanyTenderStats(ico, request.signal),
		fetchCompanyTenderCompetitors(ico, request.signal),
	]);

	return {
		ico,
		tenders: tenders.status === "fulfilled" ? tenders.value : null,
		stats: stats.status === "fulfilled" ? stats.value : null,
		competitors: competitors.status === "fulfilled" ? competitors.value : null,
	};
}

export function meta({ data: loaderData, matches }: Route.MetaArgs) {
	const company = companyFromMatches(matches);
	if (!company) return companyNotFoundMeta();
	const activity =
		(loaderData?.stats?.asSupplier.participations ?? 0) + (loaderData?.stats?.asBuyer.tenders ?? 0);
	return generateMetaTags({
		...companySocialMeta(company),
		title: `Veřejné zakázky – ${company.name} | FirmIndex`,
		description: `Zakázky firmy ${company.name} (IČO ${company.ico}) jako dodavatele i zadavatele. Výhry, nabídkové ceny, zadavatelé a konkurenti.`,
		canonical: companyCanonicalUrl(company, "zakazky"),
		noindex: activity === 0,
	});
}

export default function CompanyTendersRoute({ loaderData }: Route.ComponentProps) {
	const company = useCompanyProfile();
	// The explorer lives behind login + a per-user entitlement; only entitled
	// viewers get the cross-link (silent rollout — no teaser for the rest).
	const viewer = useViewer();
	const canUseExplorer = viewer.data?.tenderExplorerEnabled === true;
	const navigation = useNavigation();
	const revalidator = useRevalidator();
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const { tenders, stats, competitors } = loaderData;
	const totalPages = tenders ? Math.max(1, Math.ceil(tenders.total / tenders.limit)) : 1;
	const chartData = [...(stats?.asSupplier.byYear ?? [])].reverse();
	const allTenderDataUnavailable = !stats && !tenders;

	return (
		<div className="flex flex-col gap-6 pt-4">
			<header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
				<div>
					<p className="text-sm font-semibold text-primary">Účast ve veřejných zakázkách</p>
					<h1 className="text-2xl font-bold">Zakázky firmy {company.name}</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						Role zadavatele, uchazeče a vítěze jsou vedené odděleně od smluv v registru.
					</p>
				</div>
				{canUseExplorer && (
					<Link
						to={`/dashboard/zakazky?participantIco=${company.ico}`}
						className="btn btn-sm btn-secondary-soft"
					>
						Otevřít v průzkumníku
					</Link>
				)}
			</header>

			{allTenderDataUnavailable && (
				<DataLoadError
					title="Zakázky se nepodařilo načíst"
					description="Souhrn i seznam jsou teď dočasně nedostupné. Zkuste je načíst znovu."
					onRetry={() => revalidator.revalidate()}
					retrying={revalidator.state !== "idle"}
				/>
			)}

			{stats ? (
				<>
					<section
						className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
						aria-label="Statistiky veřejných zakázek"
					>
						<div className="card-surface rounded-xl p-5">
							<p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
								<Target className="h-4 w-4 text-primary" /> Účasti jako dodavatel
							</p>
							<p className="mt-2 font-mono text-2xl font-semibold tabular-nums">
								{stats.asSupplier.participations.toLocaleString("cs-CZ")}
							</p>
						</div>
						<div className="card-surface rounded-xl p-5">
							<p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
								<Trophy className="h-4 w-4 text-primary" /> Výhry
							</p>
							<p className="mt-2 font-mono text-2xl font-semibold tabular-nums">
								{stats.asSupplier.wins.toLocaleString("cs-CZ")}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">
								Úspěšnost {formatPercent(stats.asSupplier.winRate)}
							</p>
						</div>
						<div className="card-surface rounded-xl p-5">
							<p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
								<Gavel className="h-4 w-4 text-primary" /> Hodnota výher
							</p>
							<p className="mt-2 font-mono text-2xl font-semibold tabular-nums">
								{formatCurrency(stats.asSupplier.totalAwardedAsWinner)}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">Pouze částky v CZK</p>
						</div>
						<div className="card-surface rounded-xl p-5">
							<p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
								<Building2 className="h-4 w-4 text-primary" /> Zakázky jako zadavatel
							</p>
							<p className="mt-2 font-mono text-2xl font-semibold tabular-nums">
								{stats.asBuyer.tenders.toLocaleString("cs-CZ")}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">
								{formatCurrency(stats.asBuyer.totalAwardedValue)} v CZK
							</p>
						</div>
					</section>

					{chartData.length > 0 && (
						<section className="card-surface rounded-xl p-6">
							<h2 className="flex items-center gap-2 text-lg font-semibold">
								<BarChart3 className="h-5 w-5 text-primary" /> Účasti a výhry podle roku
							</h2>
							<ClientOnly
								fallback={<div className="mt-4 h-[280px] animate-pulse rounded-lg bg-muted" />}
							>
								<ResponsiveContainer width="100%" height={280}>
									<BarChart data={chartData} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
										<CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
										<XAxis dataKey="year" />
										<YAxis allowDecimals={false} />
										<Tooltip labelFormatter={(label) => `Rok ${String(label)}`} />
										<Legend />
										<Bar
											dataKey="participations"
											name="Účasti"
											fill="var(--color-chart-4)"
											radius={[4, 4, 0, 0]}
										/>
										<Bar
											dataKey="wins"
											name="Výhry"
											fill="var(--color-chart-1)"
											radius={[4, 4, 0, 0]}
										/>
									</BarChart>
								</ResponsiveContainer>
							</ClientOnly>
						</section>
					)}

					<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
						<section className="card-surface rounded-xl p-5">
							<h2 className="text-lg font-semibold">Nejčastější zadavatelé</h2>
							{stats.asSupplier.topBuyers.length === 0 ? (
								<p className="mt-3 text-sm text-muted-foreground">Zatím bez dat.</p>
							) : (
								<ol className="mt-3 flex flex-col gap-2">
									{stats.asSupplier.topBuyers.slice(0, 5).map((buyer) => (
										<li
											key={buyer.ico}
											className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 p-3 text-sm"
										>
											<span className="min-w-0">
												<span className="block truncate font-medium">
													{buyer.name ?? `IČO ${buyer.ico}`}
												</span>
												<span className="font-mono text-xs text-muted-foreground">{buyer.ico}</span>
											</span>
											<span className="shrink-0 text-right">
												<strong>{buyer.tenders}</strong>
												<span className="block text-xs text-muted-foreground">
													{buyer.wins} {winCountLabel(buyer.wins)}
												</span>
											</span>
										</li>
									))}
								</ol>
							)}
						</section>
						<section className="card-surface rounded-xl p-5">
							<h2 className="text-lg font-semibold">Nejčastější kategorie</h2>
							{stats.asSupplier.topCpv.length === 0 ? (
								<p className="mt-3 text-sm text-muted-foreground">Zatím bez dat.</p>
							) : (
								<ol className="mt-3 flex flex-col gap-2">
									{stats.asSupplier.topCpv.slice(0, 5).map((cpv) => (
										<li
											key={cpv.cpv}
											className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 p-3 text-sm"
										>
											<span>
												<span className="block font-medium">{cpv.label ?? "Kategorie CPV"}</span>
												<span className="font-mono text-xs text-muted-foreground">{cpv.cpv}</span>
											</span>
											<strong>{cpv.count}</strong>
										</li>
									))}
								</ol>
							)}
						</section>
					</div>
				</>
			) : tenders ? (
				<DataLoadError
					title="Souhrn zakázek se nepodařilo načíst"
					description="Jednotlivé zakázky zůstávají dostupné níže."
					onRetry={() => revalidator.revalidate()}
					retrying={revalidator.state !== "idle"}
				/>
			) : null}

			{(tenders || stats) && (
				<section className="flex flex-col gap-4">
					<div className="flex flex-wrap items-end justify-between gap-3">
						<div>
							<h2 className="text-lg font-semibold">Jednotlivé zakázky</h2>
							{tenders && (
								<p className="text-sm text-muted-foreground">
									{tenders.total.toLocaleString("cs-CZ")} {tenderCountLabel(tenders.total)}
								</p>
							)}
						</div>
						<Form method="get" className="flex flex-wrap items-end gap-2">
							<label className="flex flex-col gap-1 text-xs">
								<span className="font-medium">Role</span>
								<select
									className="select select-bordered select-sm"
									name="role"
									defaultValue={params.get("role") ?? "any"}
								>
									<option value="any">Všechny</option>
									<option value="buyer">Zadavatel</option>
									<option value="winner">Vítěz</option>
									<option value="bidder">Uchazeč</option>
								</select>
							</label>
							<label className="flex flex-col gap-1 text-xs">
								<span className="font-medium">Od</span>
								<input
									className="input input-bordered input-sm"
									type="date"
									name="dateFrom"
									defaultValue={params.get("dateFrom") ?? ""}
								/>
							</label>
							<label className="flex flex-col gap-1 text-xs">
								<span className="font-medium">Do</span>
								<input
									className="input input-bordered input-sm"
									type="date"
									name="dateTo"
									defaultValue={params.get("dateTo") ?? ""}
								/>
							</label>
							<button
								className="btn btn-primary btn-sm text-white"
								type="submit"
								disabled={navigation.state !== "idle"}
							>
								Filtrovat
							</button>
						</Form>
					</div>
					{!tenders && (
						<DataLoadError
							title="Seznam zakázek se nepodařilo načíst"
							description="Souhrnné statistiky zůstávají dostupné výše."
							onRetry={() => revalidator.revalidate()}
							retrying={revalidator.state !== "idle"}
						/>
					)}
					{tenders?.results.length === 0 && (
						<div className="card-surface rounded-xl p-8 text-center text-sm text-muted-foreground">
							Pro zvolenou roli a období nejsou dostupné žádné zakázky.
						</div>
					)}
					{tenders?.results.map((tender) => (
						<TenderCard key={tender.id} tender={tender} />
					))}
					{tenders && totalPages > 1 && (
						<div className="flex items-center justify-between">
							<p className="text-sm text-muted-foreground">
								Strana {tenders.page} z {totalPages}
							</p>
							<div className="flex gap-2">
								<Link
									className={`btn btn-sm btn-secondary-soft ${tenders.page <= 1 ? "pointer-events-none opacity-50" : ""}`}
									to={`?${new URLSearchParams({ ...Object.fromEntries(params), page: String(tenders.page - 1) })}`}
								>
									Předchozí
								</Link>
								<Link
									className={`btn btn-sm btn-secondary-soft ${tenders.page >= totalPages ? "pointer-events-none opacity-50" : ""}`}
									to={`?${new URLSearchParams({ ...Object.fromEntries(params), page: String(tenders.page + 1) })}`}
								>
									Další
								</Link>
							</div>
						</div>
					)}
				</section>
			)}

			{competitors && competitors.competitors.length > 0 && (
				<section className="card-surface overflow-hidden rounded-xl">
					<div className="p-5 pb-3">
						<h2 className="flex items-center gap-2 text-lg font-semibold">
							<Users className="h-5 w-5 text-primary" /> Nejčastější konkurenti
						</h2>
						<p className="text-sm text-muted-foreground">
							Firmy, které podávaly nabídku ve stejných kanonických zakázkách.
						</p>
					</div>
					<div className="overflow-x-auto">
						<table className="table">
							<thead>
								<tr>
									<th>Firma</th>
									<th className="text-right">Střetnutí</th>
									<th className="text-right">Jejich výhry</th>
									<th className="text-right">Naše výhry</th>
									<th className="text-right">Společní zadavatelé</th>
								</tr>
							</thead>
							<tbody>
								{competitors.competitors.slice(0, 10).map((competitor) => (
									<tr key={competitor.ico}>
										<td>
											<Link
												to={`/firma/${competitor.ico}`}
												className="font-medium text-primary hover:underline"
											>
												{competitor.name ?? `IČO ${competitor.ico}`}
											</Link>
											<p className="font-mono text-xs text-muted-foreground">{competitor.ico}</p>
										</td>
										<td className="text-right font-mono">{competitor.encounters}</td>
										<td className="text-right font-mono">{competitor.theirWins}</td>
										<td className="text-right font-mono">{competitor.myWins}</td>
										<td className="text-right font-mono">{competitor.sharedBuyers}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</section>
			)}
		</div>
	);
}
